/* FreshFit reviews widget (section Silk: reviews): sort, filter by stars, page numbers.
   Reviews are the JSON in [data-silk-reviews-json], written by tools/reviews/import_reviews.py.
   Also smooth-scrolls star badges ([data-silk-reviews-link]) down to the widget. */
(() => {
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const byDate = (a, b) => (a.d < b.d ? 1 : a.d > b.d ? -1 : 0);
  const SORTS = {
    featured: (a, b) => b.k - a.k || b.f - a.f || b.x - a.x || byDate(a, b),
    newest: byDate,
    highest: (a, b) => b.r - a.r || b.x - a.x || byDate(a, b),
    lowest: (a, b) => a.r - b.r || b.x - a.x || byDate(a, b),
  };
  const CHECK = '<svg class="silk-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
  const CHEV = (d) => `<svg class="silk-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;

  const stars = (r, size) => {
    let out = '';
    for (let i = 0; i < 5; i++) out += `<span class="silk-rstar"><span class="silk-rstar__fill" style="width:${Math.max(0, Math.min(1, r - i)) * 100}%"></span></span>`;
    return `<span class="silk-rstars" style="--rs:${size}px" role="img" aria-label="${r} out of 5 stars">${out}</span>`;
  };
  const date = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const card = (r) => `
    <li class="silk-review">
      <div class="silk-review__top">${stars(r.r, 16)}<time class="silk-review__date" datetime="${r.d}">${date(r.d)}</time></div>
      ${r.t ? `<h3 class="silk-review__title">${esc(r.t)}</h3>` : ''}
      ${r.b ? `<p class="silk-review__body">${esc(r.b)}</p>` : ''}
      <p class="silk-review__meta">
        <span class="silk-review__name">${esc(r.n)}</span>
        ${r.vb ? `<span class="silk-review__verified">${CHECK}Verified buyer</span>` : ''}
        ${r.v ? `<span class="silk-review__variant">${esc(r.v.replace(' / ', ' · '))}</span>` : ''}
      </p>
    </li>`;

  // Page list: first, last, current ± 1, with gaps.
  const pageList = (cur, total) => {
    const want = new Set([1, total, cur - 1, cur, cur + 1].filter((p) => p >= 1 && p <= total));
    const out = [];
    [...want].sort((a, b) => a - b).forEach((p, i, arr) => {
      if (i && p - arr[i - 1] > 1) out.push(p - arr[i - 1] === 2 ? p - 1 : 0);
      out.push(p);
    });
    return out;
  };

  function init(root) {
    const src = root.querySelector('[data-silk-reviews-json]');
    let all = [];
    try { all = JSON.parse(src.textContent); } catch (e) { return; }
    const per = parseInt(root.dataset.perPage, 10) || 5;
    const words = (root.dataset.feature || '').split(',').map((w) => w.trim().toLowerCase()).filter(Boolean);
    const rx = words.length ? new RegExp(`\\b(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'i') : null;
    all.forEach((r) => {
      r.x = r.t || r.b ? 1 : 0;
      r.k = rx && r.r >= 4 && rx.test(`${r.t} ${r.b}`) ? 1 : 0;
      r.f = r.f ? 1 : 0;
    });

    const list = root.querySelector('[data-list]');
    const pages = root.querySelector('[data-pages]');
    const showing = root.querySelector('[data-showing]');
    const clear = root.querySelector('[data-clear]');
    const clearLabel = root.querySelector('[data-clear-label]');
    const sortSel = root.querySelector('[data-sort]');
    const rows = [...root.querySelectorAll('[data-filter]')];
    const main = root.querySelector('.silk-reviews__main');
    const state = { sort: 'featured', stars: 0, page: 1 };

    const render = (scroll) => {
      const rowsNow = all.filter((r) => !state.stars || r.r === state.stars).sort(SORTS[state.sort]);
      const total = Math.max(1, Math.ceil(rowsNow.length / per));
      state.page = Math.min(state.page, total);
      const start = (state.page - 1) * per;
      list.innerHTML = rowsNow.slice(start, start + per).map(card).join('');

      const n = rowsNow.length;
      const what = state.stars ? `${state.stars}-star review${n === 1 ? '' : 's'}` : `review${n === 1 ? '' : 's'}`;
      const end = Math.min(start + per, n);
      showing.textContent = n <= per ? `Showing ${n} ${what}` : `Showing ${end - start > 1 ? `${start + 1}–${end}` : end} of ${n} ${what}`;
      clear.hidden = !state.stars;
      clearLabel.textContent = `${state.stars} star${state.stars === 1 ? '' : 's'}`;
      rows.forEach((b) => b.setAttribute('aria-pressed', String(+b.dataset.filter === state.stars)));

      if (total < 2) { pages.innerHTML = ''; } else {
        const btn = (p, label, attrs = '') => `<button type="button" data-page="${p}" ${attrs}>${label}</button>`;
        pages.innerHTML = [
          btn(state.page - 1, CHEV('m15 18-6-6 6-6'), `aria-label="Previous page"${state.page === 1 ? ' disabled' : ''}`),
          ...pageList(state.page, total).map((p) => (p === 0
            ? '<span class="silk-reviews__gap" aria-hidden="true">…</span>'
            : btn(p, p, `aria-label="Page ${p}"${p === state.page ? ' aria-current="page"' : ''}`))),
          btn(state.page + 1, CHEV('m9 18 6-6-6-6'), `aria-label="Next page"${state.page === total ? ' disabled' : ''}`),
        ].join('');
      }
      if (scroll && main.getBoundingClientRect().top < 0) main.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    pages.addEventListener('click', (e) => {
      const b = e.target.closest('[data-page]');
      if (!b || b.disabled) return;
      state.page = +b.dataset.page;
      render(true);
    });
    rows.forEach((b) => b.addEventListener('click', () => {
      const s = +b.dataset.filter;
      state.stars = state.stars === s ? 0 : s;
      state.page = 1;
      render(false);
    }));
    clear.addEventListener('click', () => { state.stars = 0; state.page = 1; render(false); });
    sortSel.addEventListener('change', () => { state.sort = sortSel.value; state.page = 1; render(false); });
    render(false);
  }

  document.querySelectorAll('[data-silk-reviews]').forEach(init);

  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-silk-reviews-link]');
    const target = a && document.getElementById('reviews');
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();
